/* Reports already paid for, listed where they were bought.
 *
 * A visitor pays, reads the report, presses back, and it is gone: the report is
 * stored against compatibility_profiles but nothing on the matching page ever
 * said so. This lists the ones that already have a generated report and opens
 * them through the same preLoadedReport hand-off ReportsPage uses, so there is
 * one way in and no second charge.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { useAuth } from "../../context/AuthContext";
import { isReportComplete } from "../../utils/reportContent";

type Row = {
  id: string;
  partner_name: string | null;
  created_at: string | null;
  ai_compatibility_report: any;
};

export default function SavedCompatReports({
  variant = "inline",
  limit = 10,
}: {
  /** "page" renders an empty-state instead of vanishing. */
  variant?: "inline" | "page";
  limit?: number;
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!user) {
      setRows([]);
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await supabase
          .from("compatibility_profiles")
          .select("id, partner_name, created_at, ai_compatibility_report")
          .eq("user_id", user.id)
          .not("ai_compatibility_report", "is", null)
          .order("created_at", { ascending: false })
          .limit(limit);
        if (!alive) return;
        // A row can hold a placeholder while generation runs. Only list what
        // will actually open.
        setRows(((data as Row[]) || []).filter((r) =>
          isReportComplete(r.ai_compatibility_report),
        ));
      } catch {
        /* listing is a convenience: a failure here must not break the page */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [user, limit]);

  if (!user) return null;
  if (loading)
    return variant === "page" ? (
      <p className="kmatch-saved__sub">Loading your reports…</p>
    ) : null;
  if (rows.length === 0)
    return variant === "page" ? (
      <p className="kmatch-saved__sub">
        No saved reports yet. Once you unlock a compatibility report it will
        live here.
      </p>
    ) : null;

  const when = (iso: string | null) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "";
    }
  };

  return (
    <section className="kmatch-saved">
      {variant === "inline" ? (
        <>
          <h2 className="kmatch-saved__h">Your saved reports</h2>
          <p className="kmatch-saved__sub">
            Already paid for. Opening one costs nothing.
          </p>
        </>
      ) : null}
      <ul className="kmatch-saved__list">
        {rows.map((r) => (
          <li key={r.id}>
            <button
              type="button"
              className="kmatch-saved__item"
              onClick={() =>
                navigate("/compatibility-report", {
                  state: {
                    preLoadedReport: r.ai_compatibility_report,
                    partnerProfileId: r.id,
                  },
                })
              }
            >
              <span className="kmatch-saved__name">
                {r.partner_name
                  ? `You & ${r.partner_name}`
                  : "Compatibility report"}
              </span>
              <span className="kmatch-saved__meta">{when(r.created_at)}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
